// Typed wrapper around /api/config — the kalibr server returns the
// Paradigm OAuth params (appUrl, clientId, callbackUrl) so the
// frontend can build the /authorize redirect URL without hard-coding
// any environment-specific values.

import { freshSessionValue, sessionFetch } from '../session';
import { currentUserId } from '../storage';
import { useRead } from '../useRead';
import { acceptProductLimits, type ProductLimitsResponse } from '../productLimits';
export interface ParadigmConfig {
  productLimits?: ProductLimitsResponse;
  appUrl: string;
  clientId: string;
  callbackUrl: string;
  /** App Insights connection string for the browser SDK; empty in local dev. */
  appInsightsConnectionString?: string;
  /** Enables the schema-less assessment weighting dev surface. */
  devSettingsEnabled?: boolean;
  /** True only for the OfSelf Working Group admins — gates Assessment Lab
   *  visibility (the link + the page) so non-admins don't see it. */
  assessmentLabAdmin?: boolean;
  /** When true, the original CV is durably stored as a rawfile alongside the
   *  parse. Gates the consent notice — a storage claim is shown to the user
   *  ONLY when this is true (otherwise the file is read and discarded). */
  resumeStoreRawfile?: boolean;
  /** Whether this deployment reads résumés (a model is configured). Off, a résumé's page says so and offers no review. */
  resumeReading?: boolean;
  /** What a card's Add to Apple Wallet does here (lib/wallet-pass mode): a signed pass, its face unsigned, or nothing. */
  walletPass?: WalletPassMode;
}
export type WalletPassMode = 'signed' | 'preview' | null;

// The signed-in user id rides the config read so the server can tell whether
// this user is an assessment admin (gates Lab visibility).
export function getCachedParadigmConfig(): ParadigmConfig | undefined {
  return freshSessionValue<ParadigmConfig>('config', currentUserId() ?? '');
}

export async function fetchParadigmConfig(): Promise<ParadigmConfig> {
  const userId = currentUserId() ?? '';
  const r = await sessionFetch('config', userId, `/api/config?user_id=${encodeURIComponent(userId)}`);
  if (!r.ok) throw new Error(`config fetch failed: HTTP ${r.status}`);
  const config = await r.json() as ParadigmConfig;
  if (config.productLimits !== undefined) acceptProductLimits(config.productLimits);
  return config;
}

/** The deployment's config as a page holds it: the session's copy at once, the fresh read once it lands. */
export const useParadigmConfig = (): ParadigmConfig | undefined => useRead(fetchParadigmConfig).data ?? getCachedParadigmConfig();

// The authorize URL is no longer built here: the server's /auth/connect builds
// it, because only the server can bind a nonce to the callback (routes/auth.js).
